/* -------------------------- Design imports start -------------------------- */

/* --------------------------- Design imports end --------------------------- */
/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement, useCallback, useState } from "react";
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import LogTool from '../../../logger/logTools'
import GetConstant from '../utils/constants'
import { useBills } from '../hooks/useContractQueries'
import { useDeleteBills, usePatchBill } from '../hooks/useContractMutations'
import SmartTable from '../../../components/SmartTable'
import { Button, Stack } from '@mui/material'
import { DeleteOutlined, EditOutlined } from '@mui/icons-material'
import { isPrivileged } from '../../../utils/functions'
import BillStaffDrawer from './BillStaffDrawer'
import { BillInfoPanelContent } from './BillInfoPanel'
import { useCountContext, useUserContext } from '../../../utils/context'
import DeleteModal from '../../../components/widgets/DeleteModal'
import FilesDrawer from '../../Filesystem/components/FilesDrawer'
import { toast } from 'react-toastify'
import { type Bill, BillInput } from '../utils/types'
import { billKeys } from '../utils/queryKeys'
import { markEntityAsRead } from '../../../api/unreadOverview'
import { type SmartTableColumnType } from '../../../utils/types'
import { type BillFilters } from '../api/contractApi'
import { deriveFilesUrl } from '../../Filesystem/utils/filesHelper';
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  selectedBill: Bill | undefined
  setSelectedBill: (value: Bill | undefined) => void
  openFilesDrawer: boolean
  setOpenFilesDrawer: (value: boolean) => void
}
/* -------------------------------------------------------------------------- */
/*                                Start Component                             */
/* -------------------------------------------------------------------------- */
export default function BillOverviewPage(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    selectedBill,
    setSelectedBill,
    openFilesDrawer,
    setOpenFilesDrawer,
  } = props
  const { t } = useTranslation()
  const log = new LogTool({ context: 'BillOverviewPage', enable: true, logLevel: 'warn' })
  const { user } = useUserContext()
  const {
    initialBills,
    setInitialBillsCount,
    setCountTotals,
    billQueryOptions,
    setBillQueryOptions,
  } = useCountContext()
  const queryClient = useQueryClient()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [, setBillDrawerOpen] = useState<boolean>(false)
  const [billStaffDrawerOpen, setBillStaffDrawerOpen] = useState<boolean>(false)
  const [openDelete, setOpenDelete] = useState<boolean>(false)
  const [filesDrawerRefreshKey, setFilesDrawerRefreshKey] = useState(0)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data States start --------------------------- */
  const [selectedBills, setSelectedBills] = useState<Bill[]>([])
  const [, setBillFormInput] = useState<any>({})
  const [billStaffFormInput, setBillStaffFormInput] = useState<any>({})
  const [newBills, setNewBills] = useState<Bill[]>(initialBills)
  /* ----------------------------- Data States end ---------------------------- */

  const patchBillMutation = usePatchBill()
  const deleteBillsMutation = useDeleteBills()

  /* ------------------------ Callback functions start ------------------------ */

  async function markBillAsRead(billItem: Bill): Promise<void> {
    const billId = Number(billItem.key)
    if (Number.isNaN(billId)) {
      log.warn('Unable to resolve bill id for marking as read', billItem)
      return
    }

    // Optimistic update directly on all bill list queries
    queryClient.setQueriesData(
      { queryKey: billKeys.lists(), exact: false },
      (oldData: any) => {
        if (!oldData || !oldData.results) return oldData
        return {
          ...oldData,
          results: oldData.results.map((item: Bill) =>
            item.key === billItem.key ? { ...item, read: true } : item
          )
        }
      }
    )

    // Update counts for tab badges
    setInitialBillsCount(prev => Math.max(0, prev - 1))
    setCountTotals(prev => Math.max(0, prev - 1))

    try {
      await markEntityAsRead('bill', billId)
    } catch (error) {
      log.error('Error marking bill as read', error)
      toast.error(t('contract:feedback.error.markBillAsRead', 'Fehler beim Aktualisieren des Lesestatus'))
    }
  }

  async function handleUpdateBill(input: Bill | BillInput): Promise<string | undefined> {
    log.debug('HandleUpdateBill', input)
    if (!input?.self) {
      throw new Error('Error updating bill: missing bill identifier')
    }

    try {
      const updatedBill = await patchBillMutation.mutateAsync({
        idOrSelf: input.self,
        data: input,
        expand: ['contract', 'editors', 'files'], // Preserve expanded fields in cache
      })

      toast.success(t('contract:feedback.success.updateBill'))
      setSelectedBills([])
      setSelectedBill(undefined)
      // Don't set refreshKey here - it will be set after files are uploaded via onFilesUploaded callback

      if (typeof updatedBill.files === 'string') {
        return updatedBill.files
      }
      if (updatedBill.self) {
        return `${updatedBill.self}files/`
      }
      return undefined
    } catch (error) {
      log.error('Error updating bill', error)
      toast.error(t('contract:feedback.error.updateBill'))
      setSelectedBills([])
      setSelectedBill(undefined)
      return undefined
    }
  }

  const handleFilesUploaded = () => {
    // Trigger refresh of FilesDrawer after files are uploaded
    setFilesDrawerRefreshKey(prev => prev + 1)
  }

  async function handleDeleteBills() {
    if (selectedBills.length === 0) {
      setOpenDelete(false)
      return
    }

    const billsToDelete = [...selectedBills]

    // Clear selection before mutation to prevent warning about selected items
    // that are no longer visible (mutation's onSuccess invalidates queries)
    flushSync(() => {
      setSelectedBills([])
      setSelectedBill(undefined)
      setOpenDelete(false)
    })

    try {
      await deleteBillsMutation.mutateAsync(billsToDelete)
      toast.success(t('contract:feedback.success.deleteBills'))
    } catch (error) {
      toast.error(t('contract:feedback.error.deleteBills'))
      log.error('Error deleting bills', error)
    }
  }

  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  // GetConstant calls hooks internally, so it cannot be inside useMemo
  const billTableColumns = GetConstant({ 
    name: 'billTableColumns', 
    newBills: newBills 
  }) as SmartTableColumnType<Bill>[]

  const billTableActions = (
    <Stack direction="row" spacing={1} alignItems="center">
      {isPrivileged(user, "STAFF") && (
        <Button
          id="editBillButton"
          size="small"
          variant="outlined"
          startIcon={<EditOutlined />}
          style={{ textTransform: 'none' }}
          onClick={() => {
            if (isPrivileged(user, "STAFF")) {
              setBillStaffFormInput(selectedBills[0])
              setBillStaffDrawerOpen(true)
            } else {
              setBillFormInput(selectedBills[0])
              setBillDrawerOpen(true)
            }
          }}
          disabled={selectedBills.length !== 1}
        >
          {t('contract:interaction.button.editBill')}
        </Button>
      )}
      {isPrivileged(user, "STAFF") && (
        <Button
          id="deleteBillButton"
          size="small"
          variant="outlined"
          style={{ textTransform: 'none' }}
          startIcon={<DeleteOutlined />}
          onClick={() => setOpenDelete(true)}
          disabled={selectedBills.length === 0}
        >
          {t('contract:interaction.button.deleteBills')}
        </Button>
      )}
    </Stack>
  )
  /* -------------------------- Render constants end -------------------------- */

  const filesDrawerFiles = (() => {
    if (!selectedBill) return []
    if (typeof selectedBill.files === 'string') return selectedBill.files
    if (Array.isArray(selectedBill.files) && selectedBill.files.length > 0) return selectedBill.files
    const filesUrl = deriveFilesUrl(selectedBill)
    return filesUrl ?? []
  })()

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <>
      <SmartTable<Bill, BillFilters>
        tableId="billTable"
        columnsConfig={{
          allColumns: billTableColumns,
          initialVisibleColumnIndices: [
            'key',
            'contract',
            'createdAt',
            'dueDate',
            'amount',
            'paid',
            'editors',
          ],
        }}
        queryConfig={{
          dataHook: useBills,
          initialQueryOptions: billQueryOptions,
        }}
        queryState={{
          options: billQueryOptions,
          setOptions: setBillQueryOptions,
        }}
        selection={{
          state: selectedBills,
          setState: setSelectedBills,
          onChange: (selected) => {
            if (selected.length === 1) {
              const record = selected[0]
              // Mark as read if unread
              if (!record.read) {
                void markBillAsRead(record)
              }
              setSelectedBill(record)
            } else if (selected.length === 0) {
              setSelectedBill(undefined)
            }
          },
        }}
        actions={billTableActions}
        customInfoPanel={(open, setOpen) => (
          <BillInfoPanelContent
            setOpenFilesDrawer={setOpenFilesDrawer}
            infoData={selectedBill}
          />
        )}
        onRow={{
          onClick: (record) => {
            setSelectedBills([record])
            setSelectedBill(record)
          },
        }}
        contextMenuOptions={[
          {
            label: t('common:interaction.button.showFiles'),
            onClick: () => setOpenFilesDrawer(true),
          },
        ]}
      />
      {isPrivileged(user, "STAFF") && (
        <BillStaffDrawer
          open={billStaffDrawerOpen}
          setOpen={setBillStaffDrawerOpen}
          formState={{
            state: billStaffFormInput,
            setState: setBillStaffFormInput
          }}
          onEditConfirm={handleUpdateBill}
          enableEditMode={selectedBills.length === 1}
          onFilesUploaded={handleFilesUploaded}
        />
      )}
      <DeleteModal
        open={openDelete}
        setOpen={setOpenDelete}
        onDelete={handleDeleteBills}
        name={t('contract:content.label.bills')}
      />
      <FilesDrawer
        key="bill-overview-files-drawer"
        open={openFilesDrawer}
        setOpen={setOpenFilesDrawer}
        /* eslint-disable-next-line @typescript-eslint/no-empty-function */
        onClose={() => {}}
        files={filesDrawerFiles}
        title={t('contract:content.label.bills')}
        refreshKey={filesDrawerRefreshKey}
      />
    </>
  )
}
